import React, { useEffect, useState } from "react";

// internal imports
import { fetchDevices, fetchConsumptionData } from "../../../services/api";
import colors from "../../../theme";
import BarGraphEnergy from "./BarGraphEnergy";
import BarGraphCost from "./BarGraphCost";

// external imports
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  InputLabel,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import EnergyConsumptionCard from "../../../components/Cards/EnergyConsumptionCard";
import { formControlLabelSx } from "../../../styles/formControlStyles";
import {
  datePickerSx,
  datePickerSlots,
  datePickerSlotProps,
} from "../../../styles/datePickerStyles";

// date format
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

const Energy = () => {
  const [view, setView] = useState("daily");
  const [chargingPoints, setChargingPoints] = useState([]);
  const [selectedChargingPoint, setSelectedChargingPoint] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const isSmallScreen = useMediaQuery("(max-width:1000px)");
  const [chartDataEnergy, setChartDataEnergy] = useState([]);
  const [chartDataCost, setChartDataCost] = useState([]);
  const [currency, setCurrency] = useState("");

  const [totalEnergy, setTotalEnergy] = useState("0");
  const [totalCost, setTotalCost] = useState("0");
  const [isConsumptionDataLoading, setIsConsumptionDataLoading] =
    useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const devices = await fetchDevices();
        setChargingPoints(devices);
        if (devices.length > 0) {
          setSelectedChargingPoint(devices[0].chargePointId);
          setCurrency(devices[0].currency);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchConsumptionDataWrapper = async () => {
      setTotalEnergy("0.00");
      setTotalCost("0");
      try {
        setIsConsumptionDataLoading(true);
        const data = await fetchConsumptionData(
          selectedChargingPoint,
          view,
          selectedDate
        );
        const totalEnergy = data?.consumptionResponse?.energy.reduce(
          (sum, current) => sum + current.energy,
          0
        );
        const totalCost = data?.consumptionResponse?.cost.reduce(
          (sum, current) => sum + current.cost,
          0
        );
        setChartDataEnergy(data?.consumptionResponse?.energy || []);
        setChartDataCost(data?.consumptionResponse?.cost || []);
        setTotalEnergy(data.totalEnergy.toFixed(2) || totalEnergy.toFixed(2));
        setTotalCost(data.totalCost.toFixed(2) || totalCost.toFixed(2));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsConsumptionDataLoading(false);
      }
    };

    if (selectedChargingPoint) {
      fetchConsumptionDataWrapper();
    }
  }, [selectedChargingPoint, view, selectedDate]);

  const handleViewChange = (event) => {
    setView(event.target.value);
    switch (event.target.value) {
      case "daily":
        setSelectedDate(dayjs());
        break;
      case "weekly":
        setSelectedDate(dayjs().startOf("week")); // Set to previous Monday
        break;
      case "monthly":
        setSelectedDate(dayjs().startOf("month"));
        break;
      case "yearly":
        setSelectedDate(dayjs().startOf("year"));
        break;
      default:
        setSelectedDate(dayjs());
    }
  };

  const handleChargingPointChange = (event) => {
    setSelectedChargingPoint(event.target.value);
    const selectedChargingPointObj = chargingPoints.find(
      (point) => point.chargePointId === event.target.value
    );
    if (selectedChargingPointObj) {
      setCurrency(selectedChargingPointObj.currency || "");
    } else {
      setCurrency("");
    }
  };

  return (
    <>
      <Box
        component="main"
        sx={{ flex: 1, px: 0, bgcolor: colors.mainContentBgColor }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            justifyContent: isSmallScreen ? "center" : "space-between",
            alignItems: isSmallScreen ? "center" : "flex-start",
            pt: 1,
            px: 4,
          }}
        >
          <FormControl
            variant="standard"
            fullWidth
            sx={{
              width: isSmallScreen ? "FullWidth" : "12rem",
              "& .MuiInput-underline:before": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.secondary,
              },
            }}
          >
            <InputLabel id="charging-point-select-label">
              Device
            </InputLabel>
            <Select
              labelId="charging-point-select-label"
              value={selectedChargingPoint}
              onChange={handleChargingPointChange}
              label="Select Device"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: "#eaeff1",
                  },
                },
              }}
            >
              {chargingPoints.map((point) => (
                <MenuItem key={point.chargePointId} value={point.chargePointId}>
                  {point.chargePointId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              marginTop: { xs: "10px", md: "20px" },
              marginBottom: "10px",
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup row value={view} onChange={handleViewChange}>
                <FormControlLabel
                  value="daily"
                  control={<Radio />}
                  label="Hours of Day"
                  sx={formControlLabelSx}
                />
                <FormControlLabel
                  value="weekly"
                  control={<Radio />}
                  label="Days of Week"
                  sx={formControlLabelSx}
                />
                <FormControlLabel
                  value="monthly"
                  control={<Radio />}
                  label="Days of Month"
                  sx={formControlLabelSx}
                />
                <FormControlLabel
                  value="yearly"
                  control={<Radio />}
                  label="Months of Year"
                  sx={formControlLabelSx}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer
              sx={{
                marginTop: 0.5,
                width: {
                  xs: "100%",
                  sm: "auto",
                  md: "auto",
                  lg: "auto",
                  xl: "auto",
                },
              }}
              components={["DatePicker"]}
            >
              <DemoItem>
                <DatePicker
                  views={
                    view === "daily" || view === "weekly"
                      ? ["year", "month", "day"]
                      : view === "monthly"
                      ? ["month", "year"]
                      : ["year"]
                  }
                  label={
                    view === "daily"
                      ? "Select Date"
                      : view === "weekly"
                      ? "Select Week"
                      : view === "monthly"
                      ? "Select Month"
                      : "Select Year"
                  }
                  value={selectedDate}
                  format={
                    view === "daily" || view === "weekly"
                      ? "DD/MM/YYYY"
                      : undefined
                  }
                  onChange={(newValue) => setSelectedDate(newValue)}
                  defaultValue={dayjs()}
                  maxDate={dayjs()} // Prevent selection of future dates
                  minDate={dayjs("2024-01-01")}
                  shouldDisableDate={(date) => {
                    const today = dayjs();
                    const isFutureDate = date.isAfter(today, "day");
                    const isNotMonday = view === "weekly" && date.day() !== 1;
                    return isFutureDate || isNotMonday;
                  }}
                  sx={{ ...datePickerSx, mt: 3, width: { md: "10rem" } }}
                  slots={datePickerSlots}
                  slotProps={{
                    ...datePickerSlotProps,
                    textField: { size: "small" },
                  }}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column-reverse",
              md: "row",
            },
            alignItems: {
              xs: "center",
              md: "flex-start",
            },
            justifyContent: "center",
          }}
        >
          {isConsumptionDataLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: { xs: "55vh", xl: "37vh" },
                width: "100%",
              }}
            >
              <CircularProgress color="success" />
            </Box>
          ) : (
            <BarGraphEnergy view={view} chartData={chartDataEnergy} />
          )}
          <Box
            sx={{
              width: {
                xs: "90%",
                md: "30%",
              },
              my: "auto",
              mr: {
                xs: 0,
                md: 10,
              },
            }}
          >
            <EnergyConsumptionCard
              type={"Total Energy"}
              value={`${totalEnergy} kWh`}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column-reverse",
              md: "row",
            },
            alignItems: {
              xs: "center",
              md: "flex-start",
            },
            justifyContent: "center",
          }}
        >
          {isConsumptionDataLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: { xs: "55vh", xl: "37vh" },
                width: "100%",
              }}
            >
              <CircularProgress color="success" />
            </Box>
          ) : (
            <BarGraphCost
              view={view}
              chartData={chartDataCost}
              currency={currency}
            />
          )}
          <Box
            sx={{
              width: {
                xs: "90%",
                md: "30%",
              },
              my: "auto",
              mr: {
                xs: 0,
                md: 10,
              },
            }}
          >
            <EnergyConsumptionCard
              type={"Total Cost"}
              value={`${totalCost} ${currency}`}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Energy;
